import React, { useEffect, useRef, useCallback, useState } from 'react';
import './activity-management.scss';
import DataGrid, {
  DataGridRef,
  Column, Toolbar, Item, LoadPanel, Sorting,
  HeaderFilter, FilterRow, Scrolling, SearchPanel,
  StateStoring, RequiredRule,
  Button as ColumnButton,
} from 'devextreme-react/data-grid';
import DevExtremePopup from 'devextreme-react/popup';
import Form, { SimpleItem, GroupItem, ButtonItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../contexts/auth';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import axios from 'axios';
import 'devextreme/data/odata/store';

interface ActivityFormData {
  Name: string;
  HourRateId: string;
  IsBillable: boolean;
}

export const ActivityManagement = () => {
  const { user, signOut } = useAuth();
  const gridRef = useRef<DataGridRef>(null);
  const [gridDataSource, setGridDataSource] = useState<DataSource>();
  const [popupVisible, setPopupVisible] = useState(false);
  const [editingOid, setEditingOid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ActivityFormData>({ Name: '', HourRateId: '', IsBillable: false });

  const authHeaders = useCallback(() => ({
    headers: {
      'Authorization': `Bearer ${user?.token}`,
      'Content-Type': 'application/json',
    },
  }), [user?.token]);

  useEffect(() => {
    const store = new ODataStore({
      version: 4,
      key: 'Oid',
      keyType: 'Guid',
      url: `${process.env.REACT_APP_URL}/api/odata/Activity`,
      errorHandler: (error: any) => {
        if (error.httpStatus === 401) {
          signOut(true);
        }
      },
      beforeSend: (e: any) => {
        e.headers['Authorization'] = `Bearer ${user?.token}`;
        if (e.params && e.params.$filter) {
          e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
        }
      },
    });

    const ds = new DataSource({
      store,
      select: ['Oid', 'Name', 'HourRateId', 'IsBillable'],
    });

    setGridDataSource(ds);
  }, [user?.token]);

  const onAddClick = useCallback(() => {
    setEditingOid(null);
    setFormData({ Name: '', HourRateId: '', IsBillable: false });
    setPopupVisible(true);
  }, []);

  const onEditClick = useCallback((e: any) => {
    const row = e.row?.data;
    if (!row) return;
    setEditingOid(row.Oid);
    setFormData({ Name: row.Name ?? '', HourRateId: row.HourRateId ?? '', IsBillable: row.IsBillable ?? false });
    setPopupVisible(true);
  }, []);

  const onDeleteClick = useCallback(async(e: any) => {
    const row = e.row?.data;
    if (!row) return;
    try {
      await axios.delete(`/api/activity/${row.Oid}`, authHeaders());
      notify('Activiteit succesvol verwijderd', 'success', 2000);
      gridRef.current?.instance().refresh();
    } catch {
      notify('Fout bij verwijderen', 'error', 3000);
    }
  }, [authHeaders]);

  const onSave = useCallback(async() => {
    if (!formData.Name) {
      notify('Naam is verplicht', 'error', 2000);
      return;
    }
    setSaving(true);
    try {
      if (editingOid) {
        await axios.put(`/api/activity/${editingOid}/Update`, formData, authHeaders());
        notify('Activiteit succesvol bijgewerkt', 'success', 2000);
      } else {
        await axios.post('/api/activity/Create', formData, authHeaders());
        notify('Activiteit succesvol aangemaakt', 'success', 2000);
      }
      setPopupVisible(false);
      gridRef.current?.instance().refresh();
    } catch {
      notify('Er is een fout opgetreden bij het opslaan', 'error', 3000);
    } finally {
      setSaving(false);
    }
  }, [editingOid, formData, authHeaders]);

  const onClearFilters = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.instance().clearFilter();
      gridRef.current.instance().option('searchPanel.text', '');
    }
  }, []);

  return (
    <div className='view activity-management'>
      <div className='view-wrapper-activity-management'>
        <DataGrid
          className='grid'
          ref={gridRef}
          noDataText=''
          focusedRowEnabled
          height='100%'
          dataSource={gridDataSource}
          allowColumnReordering
        >
          <LoadPanel showPane={false} />
          <SearchPanel width={240} visible placeholder='Zoek activiteit...' />
          <HeaderFilter visible allowSearch />
          <FilterRow visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <StateStoring
            enabled
            savingTimeout={500}
            type='localStorage'
            storageKey={`activityMgmtState_${user?.tenantName || 'all'}`}
          />

          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Activiteiten Beheer</div>
            </Item>
            <Item location='after' locateInMenu='auto'>
              <Button
                icon='plus'
                text='Activiteit'
                type='default'
                stylingMode='contained'
                onClick={onAddClick}
              />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button
                icon='refresh'
                text='Vernieuwen'
                stylingMode='text'
                onClick={() => gridRef.current?.instance().refresh()}
              />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button
                icon='clearformat'
                text='Filters wissen'
                stylingMode='text'
                onClick={onClearFilters}
              />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='Name' caption='Naam' sortOrder='asc'>
            <RequiredRule />
          </Column>
          <Column dataField='HourRateId' caption='Uurtarief ID' />
          <Column dataField='IsBillable' caption='Factureerbaar' dataType='boolean' width={130} />
          <Column type='buttons' width={80}>
            <ColumnButton icon='edit' hint='Bewerken' onClick={onEditClick} />
            <ColumnButton icon='trash' hint='Verwijderen' onClick={onDeleteClick} />
          </Column>
        </DataGrid>

        <DevExtremePopup
          visible={popupVisible}
          onHiding={() => setPopupVisible(false)}
          title={editingOid ? 'Activiteit bewerken' : 'Nieuwe activiteit'}
          showTitle
          width={480}
          height={300}
          dragEnabled={false}
          showCloseButton
        >
          <Form
            formData={formData}
            onFieldDataChanged={(e) => setFormData(prev => ({ ...prev, [e.dataField as string]: e.value }))}
            colCount={1}
          >
            <GroupItem caption='Activiteit gegevens' colCount={1}>
              <SimpleItem dataField='Name' label={{ text: 'Naam' }} isRequired />
              <SimpleItem dataField='HourRateId' label={{ text: 'Uurtarief ID' }} />
              <SimpleItem dataField='IsBillable' label={{ text: 'Factureerbaar' }} editorType='dxCheckBox' />
            </GroupItem>
            <GroupItem colCount={2} cssClass='form-buttons'>
              <ButtonItem buttonOptions={{ text: 'Annuleren', type: 'normal', stylingMode: 'outlined', onClick: () => setPopupVisible(false) }} />
              <ButtonItem buttonOptions={{ text: editingOid ? 'Opslaan' : 'Aanmaken', type: 'default', stylingMode: 'contained', disabled: saving, onClick: onSave }} />
            </GroupItem>
          </Form>
        </DevExtremePopup>
      </div>
    </div>
  );
};
